/**
 * Form for registering a new MCP server (stdio or SSE).
 *
 * On submit, calls `createMcpServer` and notifies the parent via
 * `onCreated`. The parent owns the server list — this component is
 * stateless except for the form fields themselves.
 */
import { type Component, Show, createSignal } from "solid-js";

import type {
  CreateMcpServerRequest,
  McpServerKind,
  McpServerSpec,
} from "@/api/generated";
import { createMcpServer } from "@/api/mcp";

interface AddMcpServerFormProps {
  onCreated: (server: McpServerSpec) => void;
}

const AddMcpServerForm: Component<AddMcpServerFormProps> = (props) => {
  const [name, setName] = createSignal("");
  const [kind, setKind] = createSignal<McpServerKind>("stdio");
  const [command, setCommand] = createSignal("");
  const [argsRaw, setArgsRaw] = createSignal("");
  const [url, setUrl] = createSignal("");
  const [submitting, setSubmitting] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const reset = () => {
    setName("");
    setCommand("");
    setArgsRaw("");
    setUrl("");
    setError(null);
  };

  const submit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (submitting()) return;
    setError(null);

    const trimmedName = name().trim();
    if (trimmedName.length === 0) {
      setError("name is required");
      return;
    }

    const args = argsRaw()
      .split(/\s+/)
      .map((a) => a.trim())
      .filter((a) => a.length > 0);

    const req: CreateMcpServerRequest = {
      name: trimmedName,
      kind: kind(),
      args,
    };
    if (kind() === "stdio") {
      const c = command().trim();
      if (c.length === 0) {
        setError("command is required for stdio");
        return;
      }
      req.command = c;
    } else {
      const u = url().trim();
      if (u.length === 0) {
        setError("url is required for sse");
        return;
      }
      req.url = u;
    }

    setSubmitting(true);
    try {
      const created = await createMcpServer(req);
      props.onCreated(created);
      reset();
    } catch (err) {
      const apiErr = err as { message?: string };
      setError(apiErr.message ?? String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form class="mcp-add-form" onSubmit={submit}>
      <label>
        <span>name</span>
        <input
          type="text"
          value={name()}
          placeholder="e.g. exa"
          onInput={(e) => setName(e.currentTarget.value)}
          disabled={submitting()}
          required
        />
      </label>

      <label>
        <span>transport</span>
        <select
          value={kind()}
          onChange={(e) => setKind(e.currentTarget.value as McpServerKind)}
          disabled={submitting()}
        >
          <option value="stdio">stdio</option>
          <option value="sse">sse</option>
        </select>
      </label>

      <Show when={kind() === "stdio"}>
        <label>
          <span>command</span>
          <input
            type="text"
            value={command()}
            placeholder="/usr/local/bin/exa-mcp"
            onInput={(e) => setCommand(e.currentTarget.value)}
            disabled={submitting()}
          />
        </label>
        <label>
          <span>args (space-separated)</span>
          <input
            type="text"
            value={argsRaw()}
            placeholder="--flag value"
            onInput={(e) => setArgsRaw(e.currentTarget.value)}
            disabled={submitting()}
          />
        </label>
      </Show>

      <Show when={kind() === "sse"}>
        <label>
          <span>url</span>
          <input
            type="url"
            value={url()}
            placeholder="https://mcp.example.com/sse"
            onInput={(e) => setUrl(e.currentTarget.value)}
            disabled={submitting()}
          />
        </label>
      </Show>

      <div class="mcp-add-actions">
        <button type="submit" disabled={submitting()}>
          {submitting() ? "registering…" : "register"}
        </button>
        <Show when={error()}>
          {(msg) => <span class="chat-error">{msg()}</span>}
        </Show>
      </div>
    </form>
  );
};

export default AddMcpServerForm;
